import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';
import { tinyintBoolean } from './tinyint-boolean.transformer';
import { UserEntity } from './user.entity';

/**
 * PD-14306 — a staff member following an item, to be told when it is back in
 * stock. One row per (item, user); unfollowing soft-deletes the row and
 * following again recovers it, so the unique key is never violated.
 *
 * The scheduled back-in-stock sweep is the only thing that reads stock for
 * these rows. It cannot see the moment stock changes (ERP imports write the
 * item tables without passing through this app), so the transition is kept
 * here instead: `isAwaitingRestock` is set when the sweep sees the item
 * unavailable, and the next time it sees the item available it alerts the
 * follower and clears the flag.
 *
 * Named `item_follow` rather than `item_user` (the name the other watcher
 * tables use) because a follow is a self-service toggle with restock state,
 * not a watcher list managed for other users.
 */
@Index('idx_item_follow_user', ['fkUserId'], {})
@Index('idx_item_follow_deleted', ['deletedDate'], {})
@Index('idx_item_follow_unique', ['fkItemId', 'fkUserId'], { unique: true })
@Entity('item_follow')
export class ItemFollowEntity extends BaseEntity {
	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	/** Tenant-local `user._id`, the id space the notification dispatcher uses. */
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/**
	 * Set when the item was last seen unavailable. The next sweep that sees it
	 * available alerts the follower and clears this.
	 */
	@Column('tinyint', {
		name: 'isAwaitingRestock',
		width: 1,
		default: () => "'0'",
		transformer: tinyintBoolean,
	})
	public isAwaitingRestock!: boolean;

	/**
	 * When the follower was last alerted for this item. Spaces alerts out when
	 * availability moves back and forth around zero.
	 */
	@Column('datetime', { name: 'lastNotifiedAt', nullable: true })
	public lastNotifiedAt!: Date | null;

	@ManyToOne(() => ItemEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public item!: ItemEntity;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public user!: UserEntity;
}
